export function formatDateByLocale(
  dateString = '',
  {
    locale = navigator.language,
    includeTime = false,
    timeZone,
  }: { locale?: string; includeTime?: boolean; timeZone?: string } = {},
) {
  if (!dateString) {
    return '';
  }

  const fullDateString = dateString.includes('T')
    ? dateString
    : dateString + 'T00:00:00Z';

  const date = new Date(fullDateString);

  if (isNaN(date.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  };

  if (includeTime) {
    options.hour = 'numeric';
    options.minute = 'numeric';
    options.second = 'numeric';

    if (timeZone) {
      options.timeZone = timeZone;
    }
  }

  const formatter = new Intl.DateTimeFormat(locale, options);

  return formatter.format(date);
}

export function getBenchmarkComparisonPercentage(
  streams: number,
  benchmark: number,
): number {
  return Math.round(((streams - benchmark) / benchmark) * 100);
}

export function formatPercent(percent: number, decimals = 0): string {
  return `${Math.abs(percent).toFixed(decimals)}%`;
}

export function formatPercentWithDecimal(percent: number): string {
  const absolutePercent = Math.abs(percent);
  const decimals = absolutePercent < 10 ? 1 : 0;

  return `${absolutePercent.toFixed(decimals)}%`;
}

export function getVintage(releaseDate: string | null | undefined): string {
  if (!releaseDate) {
    return '--';
  }

  const release = new Date(releaseDate + 'T00:00:00Z');

  const today = new Date();
  const todayUTC = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );

  const threeYearsAgo = new Date(todayUTC);

  threeYearsAgo.setUTCFullYear(todayUTC.getUTCFullYear() - 3);

  const tenYearsAgo = new Date(todayUTC);

  tenYearsAgo.setUTCFullYear(todayUTC.getUTCFullYear() - 10);

  if (release.getTime() >= threeYearsAgo.getTime()) {
    return 'newRelease';
  }

  if (release.getTime() >= tenYearsAgo.getTime()) {
    return 'shallowCatalog';
  }

  return 'deepCatalog';
}

export function generateVintageFilterQuery(
  vintage: 'new' | 'shallow' | 'deep' | 'all' | string,
): string {
  const now = new Date();

  const threeYearsAgo = new Date(now.toISOString());

  threeYearsAgo.setUTCFullYear(now.getUTCFullYear() - 3);

  const tenYearsAgo = new Date(now.toISOString());

  tenYearsAgo.setUTCFullYear(now.getUTCFullYear() - 10);

  switch (vintage) {
    case 'new':
      // 'newRelease' last 3 years
      return `release_date >= '${threeYearsAgo.toISOString()}'`;

    case 'shallow':
      // 'shallowCatalog' is between 3 and 10 years ago
      return `release_date >= '${tenYearsAgo.toISOString()}' AND release_date < '${threeYearsAgo.toISOString()}'`;

    case 'deep':
      // 'deepCatalog' is more than 10 years ago
      return `release_date < '${tenYearsAgo.toISOString()}'`;

    case 'all':
    default:
      return '';
  }
}

export const formatMetric = (
  metricNumber: number | undefined,
  releaseDate: string | null | undefined,
): string | null => {
  if (
    !releaseDate ||
    metricNumber === undefined ||
    !Number.isFinite(metricNumber)
  ) {
    return null;
  }

  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });

  const todayStr = dateFormatter.format(new Date());
  const releaseStr = dateFormatter.format(new Date(releaseDate + 'T00:00:00Z'));

  if (releaseStr < todayStr) {
    return new Intl.NumberFormat('en-US').format(metricNumber);
  }

  return null;
};

export const calculateWowPercentage = (
  current: number | undefined,
  previous: number | undefined,
): number => {
  if (typeof current !== 'number' || typeof previous !== 'number') {
    return 0;
  }

  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }

  const change = ((current - previous) / previous) * 100;

  return Math.round(change);
};
